import React, { useRef, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Table,
} from "reactstrap";
import { saveAs } from "file-saver";
import {
  getResidentDirectoryTemplate,
  importResidentDirectoryRoster,
} from "../TenantForm/Helpers/api";

const STATUS_META = {
  new: { label: "New", className: "rd-chip-new" },
  changed: { label: "Changed", className: "rd-chip-changed" },
  unchanged: { label: "Unchanged", className: "rd-chip-unchanged" },
  error: { label: "Errors", className: "rd-chip-error" },
};

const residentsText = (values) =>
  (values?.residents || [])
    .map((r) => [r.name, r.phone, r.email].filter(Boolean).join(" · "))
    .join(" | ") || "—";

const unitFieldsText = (values) =>
  [values?.garage_code, values?.garbage_bin, values?.composite_bin].filter(Boolean).join(" / ");

/**
 * Upload → preview → apply. Everything this modal writes goes to the Resident
 * Directory's own store — never the mastersheet. The same file is sent twice
 * (preview, then apply); the server re-parses it, so the apply is stateless.
 */
const ImportRosterModal = ({ isOpen, onClose, filterData, onImported }) => {
  const fileInputRef = useRef(null);
  const fileRef = useRef(null);
  const generationRef = useRef(0);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false); // false | "template" | "preview" | "apply"
  const [error, setError] = useState(null);

  const close = () => {
    generationRef.current += 1;
    setFileName("");
    setPreview(null);
    setBusy(false);
    setError(null);
    fileRef.current = null;
    onClose();
  };

  const downloadTemplate = async () => {
    setBusy("template");
    setError(null);
    try {
      const blob = await getResidentDirectoryTemplate({
        property_id: filterData.property_id,
        building_name: filterData.building_name,
        property_name: filterData.property_name,
      });
      saveAs(
        new Blob([blob]),
        `Resident_Directory_Template_${filterData.property_name || "property"}.xlsx`
      );
    } catch (err) {
      setError("Could not download the template.");
    } finally {
      setBusy(false);
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const gen = generationRef.current;
    fileRef.current = file;
    setFileName(file.name);
    setPreview(null);
    setError(null);
    setBusy("preview");
    try {
      const resp = await importResidentDirectoryRoster({
        file,
        property_id: filterData.property_id,
        building_name: filterData.building_name,
        mode: "preview",
      });
      if (gen !== generationRef.current) return;
      if (!resp?.success) throw new Error(resp?.message || "Preview failed");
      setPreview(resp.data);
    } catch (err) {
      if (gen !== generationRef.current) return;
      setError(err?.response?.data?.message || err.message || "Could not read the file.");
      fileRef.current = null;
      setFileName("");
    } finally {
      if (gen === generationRef.current) setBusy(false);
    }
  };

  const apply = async () => {
    const gen = generationRef.current;
    setBusy("apply");
    setError(null);
    try {
      const resp = await importResidentDirectoryRoster({
        file: fileRef.current,
        property_id: filterData.property_id,
        building_name: filterData.building_name,
        mode: "apply",
      });
      if (gen !== generationRef.current) return;
      if (!resp?.success) throw new Error(resp?.message || "Import failed");
      onImported(resp.data);
      close();
    } catch (err) {
      if (gen !== generationRef.current) return;
      setError(err?.response?.data?.message || err.message || "Import failed.");
      setBusy(false);
    }
  };

  const summary = preview?.summary || { new: 0, changed: 0, unchanged: 0, errors: 0 };
  const applicable = summary.new + summary.changed;

  return (
    <Modal isOpen={isOpen} toggle={busy === "apply" ? undefined : close} size="lg" scrollable>
      <ModalHeader toggle={busy === "apply" ? undefined : close}>Import residents from Excel</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          Fill the Resident Directory template and upload it here. Blank cells keep saved
          values; a filled name cell replaces the unit's residents; units missing from the
          file are untouched. This only updates the Resident Directory — never the
          mastersheet.
        </p>
        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          <Button color="light" onClick={downloadTemplate} disabled={busy !== false}>
            {busy === "template" ? (
              <Spinner size="sm" />
            ) : (
              <i className="mdi mdi-tray-arrow-down me-1" />
            )}
            Download template
          </Button>
          <Button color="primary" onClick={() => fileInputRef.current?.click()} disabled={busy !== false}>
            {busy === "preview" ? (
              <Spinner size="sm" />
            ) : (
              <i className="mdi mdi-tray-arrow-up me-1" />
            )}
            Choose file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={onFilePicked}
          />
          {fileName && <span className="text-muted">{fileName}</span>}
        </div>

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {preview && (
          <>
            <div className="d-flex gap-2 flex-wrap mb-2">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <span key={key} className={`rd-import-chip ${meta.className}`}>
                  {meta.label}: {key === "error" ? summary.errors : summary[key]}
                </span>
              ))}
            </div>
            <div className="rd-import-preview">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Unit</th>
                    <th>Status</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview?.rows || []).map((row) => (
                    <tr key={row.rowNo} className={row.status === "error" ? "table-danger" : undefined}>
                      <td>{row.rowNo}</td>
                      <td>{row.unitNo || "—"}</td>
                      <td>{STATUS_META[row.status]?.label ?? row.status}</td>
                      <td>
                        {row.status === "error" ? (
                          row.error
                        ) : (
                          <>
                            {row.status === "changed" && row.before && (
                              <div className="rd-import-before">
                                <s>{residentsText(row.before)}</s>
                                {unitFieldsText(row.before) && (
                                  <s className="text-muted"> — {unitFieldsText(row.before)}</s>
                                )}
                              </div>
                            )}
                            {residentsText(row.after)}
                            {unitFieldsText(row.after) && (
                              <span className="text-muted"> — {unitFieldsText(row.after)}</span>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close} disabled={busy === "apply"}>
          Cancel
        </Button>
        <Button
          color="success"
          onClick={apply}
          disabled={!preview || busy !== false || applicable === 0}
        >
          {busy === "apply" ? <Spinner size="sm" className="me-1" /> : null}
          Apply{applicable ? ` ${applicable} row${applicable === 1 ? "" : "s"}` : ""}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export { ImportRosterModal };
